import type { ReportCustomerPresetFilters } from '../types/reportCustomerPresets.js';

export type TrackedItemGrowthFilter = 'all' | 'growing' | 'stable' | 'struggling';

export interface TrackedItemColumnFilters {
  customer: string;
  itemGroup: string;
  quantityMin: string;
  quantityMax: string;
  oldDebtMin: string;
  oldDebtMax: string;
  totalDebtMin: string;
  totalDebtMax: string;
  avg3mMin: string;
  avg3mMax: string;
  avg6mMin: string;
  avg6mMax: string;
  growthStatus: TrackedItemGrowthFilter;
  growthMin: string;
  growthMax: string;
  nextOrderMin: string;
  nextOrderMax: string;
}

export const defaultTrackedItemFilters: TrackedItemColumnFilters = {
  customer: '',
  itemGroup: '',
  quantityMin: '',
  quantityMax: '',
  oldDebtMin: '',
  oldDebtMax: '',
  totalDebtMin: '',
  totalDebtMax: '',
  avg3mMin: '',
  avg3mMax: '',
  avg6mMin: '',
  avg6mMax: '',
  growthStatus: 'all',
  growthMin: '',
  growthMax: '',
  nextOrderMin: '',
  nextOrderMax: '',
};

export const SHARP_DECLINE_GROWTH_MAX = -30;
export const SHARP_DECLINE_OLD_DEBT_MIN = 2000;

const optionalNumber = (value: string): number | null => (
  value.trim() === '' ? null : Number(value)
);

const optionalText = (value: string): string | null => {
  const normalized = value.trim();
  return normalized || null;
};

export function presetFiltersFromColumnFilters(
  filters: TrackedItemColumnFilters,
): ReportCustomerPresetFilters {
  return {
    customer: optionalText(filters.customer),
    item_group: optionalText(filters.itemGroup),
    quantity_min: optionalNumber(filters.quantityMin),
    quantity_max: optionalNumber(filters.quantityMax),
    old_debt_min: optionalNumber(filters.oldDebtMin),
    old_debt_max: optionalNumber(filters.oldDebtMax),
    total_debt_min: optionalNumber(filters.totalDebtMin),
    total_debt_max: optionalNumber(filters.totalDebtMax),
    avg_3m_min: optionalNumber(filters.avg3mMin),
    avg_3m_max: optionalNumber(filters.avg3mMax),
    avg_6m_min: optionalNumber(filters.avg6mMin),
    avg_6m_max: optionalNumber(filters.avg6mMax),
    growth_status: filters.growthStatus === 'all' ? null : filters.growthStatus,
    growth_min: optionalNumber(filters.growthMin),
    growth_max: optionalNumber(filters.growthMax),
    next_order_min: optionalNumber(filters.nextOrderMin),
    next_order_max: optionalNumber(filters.nextOrderMax),
  };
}

const filterString = (value: string | number | null | undefined): string => (
  value == null ? '' : String(value)
);

export function columnFiltersFromPreset(
  filters: ReportCustomerPresetFilters,
): TrackedItemColumnFilters {
  return {
    customer: filters.customer ?? '',
    itemGroup: filters.item_group ?? '',
    quantityMin: filterString(filters.quantity_min),
    quantityMax: filterString(filters.quantity_max),
    oldDebtMin: filterString(filters.old_debt_min),
    oldDebtMax: filterString(filters.old_debt_max),
    totalDebtMin: filterString(filters.total_debt_min),
    totalDebtMax: filterString(filters.total_debt_max),
    avg3mMin: filterString(filters.avg_3m_min),
    avg3mMax: filterString(filters.avg_3m_max),
    avg6mMin: filterString(filters.avg_6m_min),
    avg6mMax: filterString(filters.avg_6m_max),
    growthStatus: filters.growth_status ?? 'all',
    growthMin: filterString(filters.growth_min),
    growthMax: filterString(filters.growth_max),
    nextOrderMin: filterString(filters.next_order_min),
    nextOrderMax: filterString(filters.next_order_max),
  };
}

export function hasPresetCondition(filters: ReportCustomerPresetFilters): boolean {
  return Object.values(filters).some((value) => value != null && value !== '');
}

export function hasInvalidPresetRange(filters: ReportCustomerPresetFilters): boolean {
  return [
    [filters.quantity_min, filters.quantity_max],
    [filters.old_debt_min, filters.old_debt_max],
    [filters.total_debt_min, filters.total_debt_max],
    [filters.avg_3m_min, filters.avg_3m_max],
    [filters.avg_6m_min, filters.avg_6m_max],
    [filters.growth_min, filters.growth_max],
    [filters.next_order_min, filters.next_order_max],
  ].some(([minimum, maximum]) => (
    minimum != null && maximum != null && minimum > maximum
  ));
}

export function matchesSharpDeclinePreset(customer: {
  tracked_item_growth_pct?: number | null;
  aged_debt_amount?: number | null;
}): boolean {
  return (
    customer.tracked_item_growth_pct != null
    && customer.tracked_item_growth_pct < SHARP_DECLINE_GROWTH_MAX
    && Number(customer.aged_debt_amount ?? 0) > SHARP_DECLINE_OLD_DEBT_MIN
  );
}
